import React from 'react';
import type { MealBreakConfig, RateMultiplier, Role } from '@/types';
import { surchargeTypeDescriptions } from '../utils/loadDescriptions';
import { calculateMealBreakMarkdownVariables } from '../utils/markdownCalculators/markdownVariables';
import { SurchargeMethodSelector as SharedSurchargeMethodSelector, type SurchargeMethod } from '../../shared/surcharge/SurchargeMethodSelector';
import { TrendingUp, Clock, Banknote, Plus, Target } from 'lucide-react';
import { buildLadderOffsetComparisonInjectable } from '../utils/ladderOffsetComparisonInjectable';
import { buildCreditExtraTimeTimelineInjectable } from '../utils/creditExtraTimeTimelineInjectable';
import { buildFixedFeeTimelineInjectable } from '../utils/fixedFeeTimelineInjectable';
import { buildMealHourlyPremiumInjectable } from '../utils/mealHourlyPremiumInjectable';
import { buildMealCountOffsetInjectable } from '../utils/mealCountOffsetInjectable';
import { allApplicableRolesAreDayRate } from '../../shared/utils';

interface Props {
  context: 'user' | 'client';
  mealConfigCustomized: Partial<Record<keyof MealBreakConfig, boolean>>;
  mealSurchargeType: MealBreakConfig['mealSurchargeType'] | null;
  onMealConfigChange: (updates: Partial<MealBreakConfig>) => void;
  safeConfig: MealBreakConfig;
  feeScheduleString: string;
  roles?: Role[];
  rateMultipliers?: RateMultiplier[];
  minimumBillableHours?: number | null;
  mealTriggerMode?: 'deadline' | 'count';
  fixedFeeStructureMode?: 'single' | 'schedule' | null;
  onFixedFeeStructureModeChange?: (mode: 'single' | 'schedule') => void;
}

const mealSurchargeMethods: SurchargeMethod[] = [
  {
    value: 'offset_overtime',
    label: 'Offset Overtime',
    description: 'Bumps your pay to the next overtime rate for the duration of the missed break. Optionally escalates further if the break is delayed long enough.',
    countModeDescription: 'For each missed meal break, bumps your shift pay up one or more overtime tiers above your prevailing rate (whole shift).',
    icon: TrendingUp
  },
  {
    value: 'guaranteed_rate',
    label: 'Guaranteed Rate',
    description: 'Guarantees a minimum rate multiplier for the missed-break duration. Independent of your current overtime position.',
    icon: Target,
    buttonLabel: 'Guaranteed Rate',
    technicalName: 'Guaranteed Rate'
  },
  {
    value: 'credit_extra_time',
    label: 'Credit Extra Time',
    description: 'Adds extra paid minutes for each interval a meal break is late — for example, 15 minutes of pay for every 30 minutes past the deadline.',
    countModeDescription: 'Adds a flat credit of extra paid minutes for each missed meal break, calculated from your hourly rate.',
    icon: Clock,
    buttonLabel: 'Credit Time',
    technicalName: 'Credit Extra Time'
  },
  {
    value: 'fixed_fee',
    label: 'Fixed Fee',
    description: 'Charges a flat dollar amount per missed break. Define one fee that repeats, or an escalating schedule where the last fee repeats for any additional violations.',
    icon: Banknote
  },
  {
    value: 'hourly_premium',
    label: 'Hourly Premium',
    description: 'Adds a flat or percentage bonus to your hourly rate for the entire time a meal break is missed.',
    countModeDescription: 'Applies a premium to your total shift pay when meal breaks are missed — either a percentage of gross pay or a dollar-per-hour surcharge.',
    icon: Plus
  },
];

export const SurchargeTypeSelector: React.FC<Props> = ({
  context,
  mealConfigCustomized,
  mealSurchargeType,
  onMealConfigChange,
  safeConfig,
  feeScheduleString,
  roles = [],
  rateMultipliers = [],
  minimumBillableHours,
  mealTriggerMode,
  fixedFeeStructureMode,
  onFixedFeeStructureModeChange,
}) => {
  const isCountMode = mealTriggerMode === 'count';

  const allDayRate = React.useMemo(
    () => allApplicableRolesAreDayRate(roles, safeConfig.applicableRoleIds),
    [roles, safeConfig.applicableRoleIds]
  );
  const thresholdFromBilling = allDayRate && minimumBillableHours != null ? minimumBillableHours : null;

  // In count mode, use countModeDescription where available
  const availableMethods = mealSurchargeMethods
    .map(m => isCountMode && m.countModeDescription
      ? { ...m, description: m.countModeDescription }
      : m
    );

  const markdownVariables = calculateMealBreakMarkdownVariables(
    safeConfig,
    feeScheduleString,
    roles,
    rateMultipliers,
    minimumBillableHours || 10 // Use actual minimum billable hours or fallback to 10
  );

  const { mealDeadlineHours, stepUpAfterMinutes, useAdditionalOffset, recurringMealDeadline, creditIntervalMinutes, timeCreditMinutes, premiumBasis, premiumType, premiumValue, premiumStacking, mealTriggerMode: configTriggerMode, requiredBreaks, requiredBreaksHalfDay, thresholdHours, fixedFeeSchedule, additionalBreakIntervalHours, extendedOvertimeEnabled, extendedOvertimeTiers, overtimeOffset } = safeConfig;
  const methodInjectables = React.useMemo(() => {
    const ladderOffsetInjectable = buildLadderOffsetComparisonInjectable({
      mealBreakConfig: safeConfig,
      rateMultipliers: rateMultipliers ?? [],
      onMealConfigChange,
      context,
    });

    const creditExtraTimeInjectable = buildCreditExtraTimeTimelineInjectable({
      mealBreakConfig: safeConfig,
      rateMultipliers: rateMultipliers ?? [],
      onMealConfigChange,
    });

    const fixedFeeInjectable = buildFixedFeeTimelineInjectable({
      mealBreakConfig: safeConfig,
      onMealConfigChange,
      fixedFeeStructureMode,
      onFixedFeeStructureModeChange,
    });

    const hourlyPremiumInjectable = buildMealHourlyPremiumInjectable({
      mealBreakConfig: safeConfig,
      rateMultipliers: rateMultipliers ?? [],
      onMealConfigChange,
      thresholdFromBilling,
    });

    const mealCountOffsetInjectable = buildMealCountOffsetInjectable({
      mealBreakConfig: safeConfig,
      rateMultipliers: rateMultipliers ?? [],
      onMealConfigChange,
      roles,
      context,
    });

    return {
      // Key matches the current method value `offset_overtime` (renamed from
      // the retired `ladder_offset` during the rate-offset unification). The
      // shared SurchargeMethodSelector looks up injectables via
      // `methodInjectables[selectedMethod.value]`, so this key must stay in
      // sync with the method value emitted by mealSurchargeMethods.
      //
      // Both the deadline-mode (`ladder_offset_comparison`) and count-mode
      // (`meal_count_offset_comparison`) graphic injectables are always
      // present in the map. The markdown description uses
      // `{{#if isCountMode}}` / `{{else}}` to select which one renders, so
      // the trigger-mode branching lives in the template rather than here.
      // This mirrors the hourly_premium wiring above.
      offset_overtime: {
        ...ladderOffsetInjectable.injectables,
        ...mealCountOffsetInjectable.injectables,
      },
      credit_extra_time: creditExtraTimeInjectable.injectables,
      fixed_fee: fixedFeeInjectable.injectables,
      hourly_premium: hourlyPremiumInjectable.injectables,
    };
  // Only rebuild when fields the graphic actually reads change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mealDeadlineHours, stepUpAfterMinutes, useAdditionalOffset, recurringMealDeadline, creditIntervalMinutes, timeCreditMinutes, premiumBasis, premiumType, premiumValue, premiumStacking, configTriggerMode, requiredBreaks, requiredBreaksHalfDay, thresholdHours, fixedFeeSchedule, additionalBreakIntervalHours, extendedOvertimeEnabled, extendedOvertimeTiers, overtimeOffset, rateMultipliers, roles, minimumBillableHours, thresholdFromBilling, onMealConfigChange, fixedFeeStructureMode, onFixedFeeStructureModeChange, context, isCountMode]);

  const hasNonStraightTimeMultipliers = rateMultipliers.some(m => m.code !== 'STRAIGHT_TIME');

  const isMethodDisabled = (method: SurchargeMethod): boolean => {
    if (method.value === 'offset_overtime') {
      return !hasNonStraightTimeMultipliers;
    }
    if (method.value === 'guaranteed_rate') {
      return !hasNonStraightTimeMultipliers || isCountMode;
    }
    return false;
  };

  const getDisabledTooltip = (method: SurchargeMethod): string => {
    if (method.value === 'offset_overtime') {
      return "Add at least one overtime multiplier to enable this method.";
    }
    if (method.value === 'guaranteed_rate') {
      if (isCountMode) {
        return "Not available in count mode — Guaranteed Rate's floor semantics don't compose with miss count.";
      }
      return "Add at least one overtime multiplier to enable this method.";
    }
    return "";
  };

  return (
    <SharedSurchargeMethodSelector
      id="meal_surcharge_type"
      title="Meal Break Surcharge Method"
      methods={availableMethods}
      selectedValue={mealSurchargeType || ''}
      onValueChange={(value) => onMealConfigChange({ mealSurchargeType: value as MealBreakConfig['mealSurchargeType'] })}
      methodDescriptions={surchargeTypeDescriptions}
      isMethodDisabled={isMethodDisabled}
      getDisabledTooltip={getDisabledTooltip}
      markdownVariables={markdownVariables}
      columns={5}
      isCustomized={mealConfigCustomized.mealSurchargeType}
      context={context}
      methodInjectables={methodInjectables}
    />
  );
};
